import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { UploadDocumentResult } from "src/core/services/document.service";
import type { DocumentField } from "src/core/utils/documents";

type ToastFn = (params: { description: string; variant?: "default" | "destructive" }) => void;

type Params = {
  requiredDocuments: readonly DocumentField[];
  toast: ToastFn;
};

/**
 * Hook utilitaire pour traiter les résultats d'upload de documents
 * et afficher un toast contenant la liste des documents en erreur.
 */
export const useUploadDocumentsErrorToast = ({ requiredDocuments, toast }: Params) => {
  const { t } = useTranslation();

  const handleUploadErrors = useCallback(
    (uploadResults: readonly UploadDocumentResult[]): boolean => {
      const failedUploads = uploadResults.filter((r) => !r.ok);

      if (failedUploads.length === 0) {
        return false;
      }

      const failedLabels = failedUploads.map((failure) => {
        const documentConfig = requiredDocuments.find((doc) => doc.code === failure.code);

        const labelKey = documentConfig?.label ?? failure.code;

        return t(labelKey);
      });

      const title = t("global.form.uploadDocuments.errorTitle", {
        count: failedLabels.length,
      });

      const description = [title, "", ...failedLabels.map((label) => `• ${label}`)].join("\n");

      toast({
        description,
        variant: "destructive",
      });

      return true;
    },
    [requiredDocuments, toast, t]
  );

  return { handleUploadErrors };
};
